import { useCallback, useState } from "react";
import {
  HERO_BASE_PRESETS,
  HERO_STILL_VIEWS,
  customHeroIds,
  heroBaseKey,
  heroOwnedKeys,
  heroStillKey,
  heroViewEditable,
  heroViewSubject,
  nextHeroBaseId,
  walkBakePlan,
  walkBakeTargets,
  walkPlanSummary,
  type AssetSpec,
} from "@greg-chick/shared";
import { SlotUploader } from "./SlotUploader";
import { ArtDraftBox } from "./ArtDraftBox";
import { ActionButton } from "./ActionButton";
import { StripPlay } from "./StripPlay";
import { bakeWalkStrip } from "../lib/bakeWalk";
import { flipAssetEntry } from "../lib/mirrorImage";
import { loadImage } from "../lib/strip";
import { uploadAsset } from "../lib/contentApi";
import type { ManifestEntry } from "../lib/contentApi";

/**
 * 🧒 **히어로 캐릭터 만들기·지우기** — 아이가 「완성 캐릭터」에서 고르는 친구들.
 *
 * ## 몇 명이든 만들 수 있다
 * 예전엔 빈 자리 넷(`explorer_f`~`i`)을 카탈로그에 뚫어 두고 그만큼만 채우게 했다.
 * 목록이 곧 검증이었기 때문인데, 지금은 `isBase` 가 **모양으로** 검증하고 그림은
 * 매니페스트에서 찾는다 — 목록에 없는 id 가 와도 최악이 「기본 아바타로 보인다」다.
 * 그래서 자리를 미리 뚫어 둘 이유가 없어졌다(자세히는 shared `isBase`).
 *
 * ## 그림이 들어간 자리만 아이에게 보인다
 * 빈 자리는 아이 화면 선택지에 안 나온다(`pickableBases`). 정면 그림을 올리는 순간 나타난다.
 *
 * ## 지울 때는 **무엇이 지워지는지 먼저 보여준다**
 * 캐릭터 하나는 그림 자리를 네 개 쓴다(정지·앞·옆·뒤). 「지울까요?」만 물으면 운영자는
 * 무엇이 사라지는지 모른 채 누른다. 그리고 **그 캐릭터를 고른 아이가 어떻게 되는지**가
 * 사실 더 중요하다 — 그건 화면 밖 일이라 더더욱 미리 말해야 한다.
 */
/** 캐릭터 하나가 쓰는 그림 자리 — **[🧸 캐릭터]의 지우기도 이 표를 쓴다**(지워지는 목록이 갈리면 안 된다). */
export const HERO_DIRS = [
  { sfx: "", label: "서 있는 모습", need: true, hint: "고르기 화면과 첫 화면에 나와요" },
  { sfx: ".down", label: "앞으로 걷기", need: false, hint: "네 칸이 가로로 이어진 그림" },
  { sfx: ".side", label: "옆으로 걷기", need: false, hint: "왼쪽은 자동으로 뒤집어요" },
  { sfx: ".up", label: "뒤로 걷기", need: false, hint: "" },
];

/** 운영자가 만든 캐릭터 목록은 **매니페스트가 정한다** — 코드가 자리를 세지 않는다. */
const labelOf = (id: string) => HERO_BASE_PRESETS.find((p) => p.id === id)?.label ?? id;

/**
 * 지우기 전에 보여줄 말 — **무엇이 사라지는지 한 줄씩.**
 *
 * 「지울까요?」만 물으면 운영자는 무엇이 없어지는지 모른 채 누른다. 캐릭터 하나는 그림 자리를
 * 네 개 쓰는데(정지·앞·옆·뒤) 그게 한꺼번에 사라진다.
 *
 * 그리고 **그 캐릭터를 고른 아이가 어떻게 되는지**가 사실 더 중요하다 — 화면 밖 일이라
 * 안 적으면 아무도 모르고, 나중에 "아이 캐릭터가 바뀌었다"는 신고로 돌아온다.
 */
export function heroDeletePrompt(
  nick: string,
  dirLabels: readonly string[],
  builtin = false,
): string {
  return [
    `'${nick}' 을(를) 지울까요?`,
    "",
    dirLabels.length ? `지워지는 그림 ${dirLabels.length}장:` : "아직 올린 그림이 없어요.",
    ...dirLabels.map((l) => `  · ${l}`),
    "",
    "이 캐릭터를 고른 아이는 다음에 들어올 때",
    "기본 아바타(직접 꾸미기)로 보여요.",
    "",
    // 되돌리는 무게가 다르다 — 올린 파일을 다시 올리는 것과, 그림을 새로 만드는 것.
    builtin
      ? "⚠️ 처음부터 들어 있던 캐릭터예요." + "\n" + "되돌리려면 그림을 새로 만들어 올려야 해요."
      : "되돌리려면 그림을 다시 올리면 돼요.",
  ].join("\n");
}

export function HeroMaker({
  spec,
  assets,
  onChange,
  name,
  setName,
}: {
  spec?: AssetSpec;
  assets: Record<string, ManifestEntry | undefined>;
  onChange: (key: string, entry: ManifestEntry | null) => void;
  name: string;
  setName: (v: string) => void;
}) {
  const used = customHeroIds(Object.keys(assets));
  // 새로 만들 자리는 늘 하나 준비돼 있다 — 안 쓰는 가장 작은 번호.
  const fresh = nextHeroBaseId(Object.keys(assets));
  /**
   * 🔴 **방금 만든 아이에 머문다.**
   *
   * 빈 자리만 보고 있으면, 서 있는 그림을 올리는 **순간** 그 자리가 「쓰는 자리」가 되어
   * 다음 빈 번호로 넘어간다. 그러면 방금 만든 아이의 옆·뒷모습·걷기 칸이 **화면에서 사라진다** —
   * 예전 [🎬 걷기 만들기] 버튼이 그래서 사실상 눌릴 수 없는 자리에 있었다.
   * 지운 뒤에는 다시 빈 자리로 돌아간다(`used` 에 없으면 폴백).
   */
  const [made, setMade] = useState<string | null>(null);
  const target = made && used.includes(made) ? made : fresh;
  /**
   * 🎨 AI·붙여넣기로 받은 그림. **칸마다 따로** 담는다 — 서 있는 모습·옆모습·뒷모습이
   * 각자 AI 칸을 갖고 있어서, 하나로 두면 옆모습을 만들었는데 서 있는 칸에 들어간다.
   */
  const [drop, setDrop] = useState<Record<string, File | null>>({});
  const dropTo = (key: string) => (f: File) => setDrop((d) => ({ ...d, [key]: f }));

  /**
   * 🖼️ 참고 그림 후보. **지금 만드는 캐릭터의 서 있는 모습**이 맨 앞이다 —
   * 「이 아이의 옆모습」을 그리게 하려면 그 아이를 보여 주는 수밖에 없다.
   */
  const heroRefs = [
    ...(assets[heroBaseKey(target)]?.url
      ? [
          {
            key: heroBaseKey(target),
            url: assets[heroBaseKey(target)]!.url,
            label: "지금 만드는 아이",
          },
        ]
      : []),
    // 🔴 지금 만드는 아이는 **위에서 이미 넣었다.** 안 빼면 같은 그림이 두 번 나오고,
    //    둘 다 켜진 것처럼 보여서 참고를 두 장 보내는 줄 알게 된다(실제로도 두 장 간다).
    ...used
      .filter((id) => id !== target)
      .map((id) => ({
        key: heroBaseKey(id),
        url: assets[heroBaseKey(id)]?.url ?? "",
        label: assets[heroBaseKey(id)]?.alt || labelOf(id),
      }))
      .filter((r) => !!r.url),
  ];

  /**
   * 🎬 **정지 그림에서 걷기를 굽는다** — 방향마다 어느 그림을 쓸지는 `walkBakePlan`(shared)이 정한다.
   *
   * 옆·뒷모습이 있으면 그것으로, 없으면 **서 있는 모습**으로 만든다. 옆·뒤 그림이 없다고
   * 아무것도 안 만드는 것보다 정면으로라도 움직이는 편이 낫다 — 걷기 그림이 아예 없으면
   * 아이 화면에서는 서 있는 그림 그대로라 **제자리에서 미끄러진다.**
   *
   * 계획을 화면에도 그대로 보여 준다. 예전엔 눌러야만 「이미 다 있어요」를 알 수 있었고,
   * 새로 넣은 옆모습이 왜 안 쓰이는지는 끝내 알 수 없었다.
   */
  const plan = target ? walkBakePlan(heroBaseKey(target), (k) => assets[k], HERO_DIRS) : [];
  const todo = walkBakeTargets(plan);
  /** 지금 이 아이가 가진 걷기 그림들 — 만든 뒤 **움직여 보여 주려고** 모은다. */
  const builtWalks = target
    ? plan
        .map((s) => ({ ...s, url: assets[heroBaseKey(target) + s.sfx]?.url }))
        .filter((s): s is typeof s & { url: string } => !!s.url)
    : [];

  /**
   * ↔ 옆모습을 좌우로 뒤집어 다시 올린다.
   *
   * 걷기까지 이미 구웠다면 그것도 같이 뒤집는다 — 안 그러면 **정지 그림만 고쳐지고
   * 아이 화면은 그대로**다(고쳤는데 안 바뀐다는 그 모양이다).
   */
  const flipStill = useCallback(
    async (key: string) => {
      const src = assets[key]?.url;
      if (!src) throw new Error("먼저 옆모습 그림을 넣어 주세요.");
      const walkKey = key.replace(/\.still$/, "");
      for (const k of [key, ...(assets[walkKey]?.url ? [walkKey] : [])]) {
        // 뒤집는 일 자체는 **한 곳**에만 둔다(lib/mirrorImage) — 자리마다 다시 짜면
        // 한쪽만 고쳐지고, 스트립을 통째로 뒤집는 실수가 거기서 되살아난다
        const next = await flipAssetEntry(k, assets[k]!.url, { loadImage, upload: uploadAsset });
        onChange(k, { ...assets[k]!, ...next });
      }
    },
    [assets, onChange],
  );

  const bakeWalks = useCallback(
    async (id: string, force = false) => {
      const standing = assets[heroBaseKey(id)]?.url;
      if (!standing) throw new Error("먼저 「서 있는 모습」을 올려 주세요. 그 그림으로 만들어요.");
      const steps = walkBakeTargets(walkBakePlan(heroBaseKey(id), (k) => assets[k], HERO_DIRS), force);
      if (steps.length === 0) {
        // 🔴 「이미 다 있어요」로 끝내지 않는다 — **왜** 안 만드는지가 계획에 적혀 있다.
        throw new Error(
          "새로 만들 것이 없어요." + "\n" +
            walkBakePlan(heroBaseKey(id), (k) => assets[k], HERO_DIRS)
              .map((s) => `· ${s.label}: ${s.why}`)
              .join("\n"),
        );
      }
      for (const step of steps) {
        const src = assets[step.from]?.url;
        if (!src) throw new Error(`${step.label}의 바탕 그림을 못 찾았어요.`);
        const blob = await bakeWalkStrip(await loadImage(src));
        if (!blob) throw new Error("그림에서 사람 모양을 못 찾았어요. 배경이 투명한지 확인해 주세요.");
        const key = heroBaseKey(id) + step.sfx;
        const file = new File([blob], `${id}${step.sfx}.webp`, { type: "image/webp" });
        const url = await uploadAsset(key, file);
        // 🔴 **어느 그림에서 구웠는지 남긴다.** 이게 없으면 다음번에 새 옆모습을 넣어도
        //    「이미 있다」로 넘어가거나, 손으로 올린 걷기를 덮어쓰게 된다(shared `walkBakePlan`).
        onChange(key, { url, alt: assets[heroBaseKey(id)]?.alt, bakedFrom: step.from });
      }
    },
    [assets, onChange],
  );

  if (!spec) return <p className="hint">⚠️ 히어로 그림 슬롯 정의가 없어요(content/asset-slots.json).</p>;

  /** 지우기 전에 **무엇이 사라지는지** 한 줄씩 보여준다. */
  /**
   * 🙈 이 아이의 그림 **전부**를 내리거나 올린다.
   *
   * 🔴 서 있는 그림만 내리면 걷기 그림이 매니페스트에 남는다. 아이 화면에는 안 보이니
   * 당장은 같아 보이지만, 나중에 다시 올릴 때 「반쪽만 돌아오는」 자리가 생긴다.
   * 목록은 지우기와 **같은 함수**를 쓴다(`heroOwnedKeys`).
   */
  function setHeroOnWeb(id: string, on: boolean) {
    for (const k of heroOwnedKeys(heroBaseKey(id), (key) => !!assets[key]?.url, HERO_DIRS)) {
      onChange(k.key, { ...assets[k.key]!, offWeb: on ? undefined : true });
    }
  }

  /** 🏷 이름만 바꾼다 — 그림은 그대로. 고르기 화면에 나오는 이름이 이것이다. */
  function renameHero(id: string) {
    const cur = assets[heroBaseKey(id)]?.alt ?? "";
    const next = window.prompt("이 아이의 이름을 무엇으로 바꿀까요?", cur);
    if (next === null) return; // 취소
    const trimmed = next.trim().slice(0, 20);
    if (!trimmed || trimmed === cur) return;
    // 이름은 그림마다 붙어 있다 — 한 자리만 고치면 걷기 그림에 옛 이름이 남는다.
    for (const k of heroOwnedKeys(heroBaseKey(id), (key) => !!assets[key]?.url, HERO_DIRS)) {
      onChange(k.key, { ...assets[k.key]!, alt: trimmed });
    }
    if (made === id) setName(trimmed);
  }

  function removeHero(id: string) {
    // 지워지는 목록은 [🧸 캐릭터]와 **같은 함수**로 만든다 — 갈리면 지운 그림이 남는다.
    const present = heroOwnedKeys(heroBaseKey(id), (k) => !!assets[k]?.url, HERO_DIRS);
    const nick = assets[heroBaseKey(id)]?.alt || labelOf(id);
    if (!window.confirm(heroDeletePrompt(nick, present.map((d) => d.label)))) return;
    present.forEach((d) => onChange(d.key, null));
  }

  return (
    <div className="cres__make">
      <h2 className="as-sec__h">
        {made && used.includes(made)
          ? `만드는 중: ${assets[heroBaseKey(made)]?.alt || name.trim() || "새 캐릭터"}`
          : "새 히어로 캐릭터 만들기"}
      </h2>
      <label className="cres__name">
        이름
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예: 파란 모자 탐험가"
          maxLength={20}
        />
      </label>

      {/* 🔢 **셋으로 나눈다.** 예전엔 그림 칸이 일곱이었고 「앞으로 걷기」와 「앞모습」이
          나란히 있었다 — 이름이 거의 같은데 하나는 만드는 것, 하나는 만들어지는 것이었다.
          만들어지는 것(걷기 네 칸)은 여기서 안 보여 준다. 넣는 것만 보이면 된다. */}
      <section className="cres__step">
        <h3 className="cres__step-h">
          <span className="cres__step-n">1</span> 서 있는 모습 <em>꼭 필요해요</em>
        </h3>
        <p className="hint">정사각 투명 그림 — 아이 한 명이 가운데. 이것만 올려도 아이 화면에 나와요.</p>
        {target ? (
          <div className="grid">
            <SlotUploader
              slotKey={heroBaseKey(target)}
              label="서 있는 모습"
              spec={spec}
              entry={assets[heroBaseKey(target)]}
              onChange={(k, e) => {
                onChange(k, e ? { ...e, alt: name.trim() || e.alt } : e);
                // 그림이 들어온 순간 이 아이에 머문다(안 그러면 다음 빈 번호로 넘어간다).
                setMade(e ? target : null);
              }}
              alt={name.trim() || undefined}
              incoming={drop[heroBaseKey(target)] ?? null}
            />
          </div>
        ) : null}
        {/* 🖼️ 참고 그림: 이미 만든 다른 캐릭터들(결 맞추기). */}
        <ArtDraftBox kind="hero" name={name} onPick={dropTo(heroBaseKey(target ?? ""))} refs={heroRefs} />
      </section>

      {target && assets[heroBaseKey(target)]?.url ? (
        <>
          <section className="cres__step">
            <h3 className="cres__step-h">
              <span className="cres__step-n">2</span> 걷는 모습 <em>없어도 돼요</em>
            </h3>
            <p className="hint">
              없으면 <b>서 있는 모습 그대로</b> 걷게 만들어요 — 옆으로 걸어도 얼굴이 이쪽을 봐요.
              🤖 를 누르면 서 있는 모습을 참고로 <b>같은 아이</b>를 그려 줘요.
            </p>
            <div className="grid">
              {HERO_STILL_VIEWS.map((v) => {
                const key = heroStillKey(heroBaseKey(target), v.sfx);
                return (
                  <div key={v.sfx} className="cres__still">
                    <SlotUploader
                      slotKey={key}
                      label={v.label}
                      spec={spec}
                      entry={assets[key]}
                      onChange={(k, e) => onChange(k, e ? { ...e, alt: name.trim() || e.alt } : e)}
                      alt={name.trim() || undefined}
                      incoming={drop[key] ?? null}
                    />
                    {/* ↔ **어느 쪽을 보고 있나.** 옆모습이 반대로 그려지면 아이가 가는 쪽의
                        반대를 보며 걷는다 — 실제로 완성 캐릭터 다섯이 그랬고 아무 에러도 안 났다.
                        말로 설명하는 대신 **아이가 볼 그대로** 두 장을 나란히 보여 준다. */}
                    {v.sfx === ".side" && assets[key]?.url ? (
                      <div className="cres__face">
                        <figure>
                          <img src={assets[key]!.url} alt="오른쪽으로 갈 때" />
                          <figcaption>→ 오른쪽</figcaption>
                        </figure>
                        <figure>
                          <img className="cres__face--flip" src={assets[key]!.url} alt="왼쪽으로 갈 때" />
                          <figcaption>← 왼쪽</figcaption>
                        </figure>
                        <p className="hint">
                          가는 쪽을 보고 있나요?{" "}
                          <ActionButton
                            className="btn btn--sm"
                            onAction={() => flipStill(key)}
                            doneLabel="뒤집었어요"
                          >
                            ↔ 뒤집기
                          </ActionButton>
                        </p>
                      </div>
                    ) : null}
                    <ArtDraftBox
                      kind="hero"
                      name={name}
                      onPick={dropTo(key)}
                      refs={heroRefs}
                      preset={{
                        subject: heroViewSubject(v, name),
                        editable: (style) => heroViewEditable(v, name, style),
                        // 「같은 아이」를 그리려면 그 아이를 **꼭** 보여 줘야 한다 — 미리 켜 둔다.
                        refs: [heroBaseKey(target)],
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </section>

          <section className="cres__step">
            <h3 className="cres__step-h">
              <span className="cres__step-n">3</span> 걷게 만들기
            </h3>
            {/* 🔴 누르기 **전에** 무엇이 일어날지 한 줄로. 예전엔 세 줄짜리 표를 늘 펼쳐 놨는데,
                정작 「무엇을 눌러야 하는지」가 그 글에 묻혔다. 방향별 사정은 접어 둔다. */}
            <p className="cres__sum">{walkPlanSummary(plan)}</p>
            <p className="hint">
              <ActionButton
                className="btn btn--primary"
                disabled={todo.length === 0}
                onAction={() => bakeWalks(target)}
                doneLabel="다 됐어요 — 아래에서 움직여 봐요"
              >
                🎬 걷게 만들기{todo.length > 0 ? ` (${todo.length})` : ""}
              </ActionButton>
            </p>

            {/* 🔴 **만든 결과를 눈으로.** 예전엔 누르면 그림 자리가 조용히 채워질 뿐이라
                「아무 변화가 없다」로 보였다. 걷는 그림은 **움직여 봐야** 잘 됐는지 안다. */}
            {builtWalks.length > 0 ? (
              <div className="cres__result">
                <p className="cres__result-h">이렇게 걸어요</p>
                <div className="cres__result-row">
                  {builtWalks.map((m) => (
                    <figure key={m.sfx}>
                      <StripPlay url={m.url} />
                      <figcaption>{m.short}</figcaption>
                    </figure>
                  ))}
                </div>
                <p className="hint">
                  아이 화면에 나가려면 <b>[🚀 웹에 올리기]</b>까지 눌러 주세요. 마음에 안 들면{" "}
                  <ActionButton
                    className="btn btn--sm"
                    onAction={() => bakeWalks(target, true)}
                    doneLabel="다시 만들었어요"
                  >
                    🔄 다시 만들기
                  </ActionButton>{" "}
                  직접 올리신 걷기 그림은 그대로 둬요.
                </p>
              </div>
            ) : null}

            <details className="cres__plan-d">
              <summary>방향별로 어떻게 되는지 보기</summary>
              <ul className="cres__plan">
                {plan.map((s) => (
                  <li key={s.sfx} data-act={s.action}>
                    <b>{s.short}</b> {s.why}
                  </li>
                ))}
              </ul>
            </details>
          </section>

          <p className="hint">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                setMade(null);
                setName("");
              }}
            >
              ➕ 다른 캐릭터 만들기
            </button>{" "}
            이 아이는 다 됐어요. 빈 자리로 넘어가요.
          </p>
        </>
      ) : null}

      {used.length > 0 ? (
        <>
          <h2 className="as-sec__h">이미 만든 히어로</h2>
          <div className="grid">
            {used.map((id) => (
              <div key={id} className="cres__still">
                <SlotUploader
                  slotKey={heroBaseKey(id)}
                  label={assets[heroBaseKey(id)]?.alt || labelOf(id)}
                  spec={spec}
                  entry={assets[heroBaseKey(id)]}
                  onChange={onChange}
                  onRemove={() => removeHero(id)}
                  // 🙈 캐릭터 하나는 그림 자리를 여럿 쓴다(서 있기·걷기 셋·걷는 그림 셋).
                  //    서 있는 그림만 내리면 걷기가 남아 나중에 **반쪽만 돌아온다.**
                  onSetOnWeb={(onWeb) => setHeroOnWeb(id, onWeb)}
                />
                <div className="cres__acts">
                  {/* 🔴 **다시 돌아올 길.** 화면을 닫았다 켜면 「만드는 중」이 풀린다 —
                      이 버튼이 없으면 어제 만든 아이에게 옆·뒷모습을 더할 방법이 없다. */}
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => {
                      setMade(id);
                      setName(assets[heroBaseKey(id)]?.alt ?? "");
                    }}
                    disabled={made === id}
                  >
                    {made === id ? "✏️ 만드는 중" : "✏️ 이어서 만들기"}
                  </button>
                  <button type="button" className="btn btn--sm" onClick={() => renameHero(id)}>
                    🏷 이름 바꾸기
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="hint">
            [✏️ 이어서 만들기]를 누르면 위에서 <b>옆·뒷모습</b>을 더하거나 <b>걷기</b>를 다시
            만들 수 있어요. 걷기 그림을 직접 그려 올리시려면 [🧸 캐릭터] → [🎞️ 움직임]에서요.
          </p>
        </>
      ) : null}
    </div>
  );
}
